import { captureException } from '@sentry/react';
import toast from 'react-hot-toast';

import { ConfirmDialog } from '../../../components/dialog/ConfirmDialog';
import { getDisplayError } from '../../../utils/get-display-error';
import { setOrderSelfBillExclusion } from '../models';
import { canExcludeFromSelfBilling, type SelfBillIneligibilityReason } from './OrderSelfBillEligibilityNote';

interface Props {
  order: {
    id: string;
    orderNumber: string | null;
    selfBillExcluded: boolean;
    purchaseLocked: boolean;
    selfBillEligibility: { reasons: string[] };
  };
  onChange: () => void | Promise<void>;
}

/** Order-page counterpart of "Permanent verwijderen" on a self-bill, and the way back. */
export const OrderSelfBillExclusionAction: React.FC<Props> = ({ order, onChange }) => {
  const reasons = order.selfBillEligibility.reasons as SelfBillIneligibilityReason[];

  const setExcluded = async (excluded: boolean) => {
    try {
      await setOrderSelfBillExclusion(order.id, excluded);
      toast.success(excluded ? 'Order uitgesloten van self billing' : 'Order terug opgenomen in self billing');
      await onChange();
    } catch (err) {
      captureException(err);
      toast.error(`Kon order niet aanpassen: ${getDisplayError(err)}`);
      throw err;
    }
  };

  if (order.selfBillExcluded) {
    return (
      <ConfirmDialog
        triggerText="Opnemen in self billing"
        title={`Order ${order.orderNumber ?? '-'} terug opnemen?`}
        submitText="Opnemen"
        description="De order kan opnieuw op een ritoverzicht of self billing komen."
        onSubmit={() => setExcluded(false)}
      />
    );
  }

  if (!canExcludeFromSelfBilling(reasons, order.purchaseLocked)) {
    return null;
  }

  return (
    <ConfirmDialog
      triggerText="Uitsluiten van self billing"
      title={`Order ${order.orderNumber ?? '-'} uitsluiten?`}
      submitText="Uitsluiten"
      description={
        reasons.includes('AlreadyOnSelfBill')
          ? 'De order gaat uit het concept en komt niet terug in een volgende run. Een concept zonder andere orders wordt verworpen.'
          : 'De order komt niet op een ritoverzicht of self billing.'
      }
      onSubmit={() => setExcluded(true)}
    />
  );
};
